import { Component, OnInit, HostListener, ViewChild, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { InspectionService, LocalEntityService, MachineFamillyService, MachineModuleService } from '../../shared/services/index';
import {
  ApplicationSubType,
  Inspection,
  InspectionFilter,
  InspectionFilterType,
  InspectionOrderField,
  Person,
  Search,
  SearchFilter
} from '../../shared/models/index';
import { BasePageComponent } from '../../base-page.component';
import * as _ from 'lodash';
import { PeopleService } from '../../shared/services/people.service';
import { InspectionListMenuComponent } from '../../menus/inspection-list.menu';
import { Observable, timer, zip } from 'rxjs';
import { switchMap, takeWhile, tap } from 'rxjs/operators';
import { ModalComponent } from '../../modal/modal.component';
import { InspectionFilterModalComponent } from '../../shared/components';
import { ApplicationType } from '../../utils';

@Component({
  templateUrl: './list.component.html',
  standalone: false
})
export class InspectionListPageComponent extends BasePageComponent implements OnInit, OnDestroy {
  readonly pageSize = 50;
  readonly defaultFilters: SearchFilter[] = [
    { type: InspectionFilterType.contentType, value: '100' },
    { type: InspectionFilterType.inspectorInspections, value: '100' },
    {
      type: InspectionFilterType.applicationSubType,
      value: this.isIntallation ? `${ApplicationSubType.ParentInstallation}` : ''
    }
  ];
  readonly additionalFilters: SearchFilter[] = [];
  filter: InspectionFilter = {};
  page = 0;
  filteredInspections: Inspection[];
  creatingInspections: Inspection[];
  offlineInspection: Inspection;
  search: Search;
  people: Person[];
  productLines: any[];
  statusList: number[];
  localEntities: any[];
  equipmentTypes: any[];
  loading: boolean = true;
  isListComplete = false;
  alive = true;
  clientNumber: string;
  machineSerial: string;
  @ViewChild('filterModal', { static: true }) filterModal: ModalComponent;

  constructor(
    private inspectionService: InspectionService,
    private peopleService: PeopleService,
    private machineFamillyService: MachineFamillyService,
    private localEntityService: LocalEntityService,
    private machineModuleService: MachineModuleService,
    private router: Router,
    private route: ActivatedRoute,
    location: Location
  ) {
    super(location);
  }

  @HostListener('window:beforeunload', ['$event']) beforeunload($event) {
    if (this.offlineInspection && this.offlineInspection.updatedOffline) {
      const message = `You have a locally registered inspection, don't forget to synchronize it sometimes.`;
      $event.preventDefault();
      $event.returnValue = message;
    }
  }

  @HostListener('window:offline', ['$event']) onOffline(evt) {
    this.initInspections();
  }

  @HostListener('window:online', ['$event']) onOnline(evt) {
    this.initInspections();
  }

  openInspection(inspection: Inspection): void {
    InspectionService.machineSerial = this.machineSerial;
    InspectionService.clientNumber = this.clientNumber;
    if (inspection.applicationSubType === ApplicationSubType.ParentInstallation) {
      this.router.navigate([this.inspectionsRoute, inspection.id, 'inspections']);
      return;
    }
    this.router.navigate([this.inspectionsRoute, inspection.id]);
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.clientNumber = params.get('clientNumber');
      this.machineSerial = params.get('machineSerial');
      if (this.machineSerial) {
        this.additionalFilters.push({ type: InspectionFilterType.equipmentNumber, value: this.machineSerial });
        this.initialize();
        return;
      }

      if (this.clientNumber) {
        this.machineModuleService.getCustomerMachines(this.clientNumber, 1, 100000).subscribe((machines) => {
          if (!machines.length) {
            this.filteredInspections = [];
            this.loading = false;
            return;
          }

          machines.forEach((m) => this.additionalFilters.push({ type: InspectionFilterType.equipmentNumber, value: m.serialNumber }));
          this.initialize();
        });
      } else {
        this.initialize();
      }
    });
  }

  private initialize(): void {
    this.initInspections();
    this.initData();
  }

  initInspections() {
    if (!this.isConnection) {
      this.inspectionService.getInspections().subscribe((data) => {
        this.offlineInspection = data.find((inspection) => inspection.id === this.offlineInspectionId);
        this.filteredInspections = data;
        this.loading = false;
      });
      return;
    }

    if (this.inspectionService.filter) {
      this.filter = this.inspectionService.filter;
    }
    zip(this.getInspections(), this.inspectionService.getOfflineInspection()).subscribe(([inspections, offlineInspection]) => {
      this.offlineInspection = offlineInspection;
      this.filteredInspections = this.offlineInspection ? [this.offlineInspection, ...inspections] : inspections;
    });

    timer(0, 10000)
      .pipe(
        takeWhile(() => this.alive),
        switchMap(() =>
          this.inspectionService.getCreatingInspections({
            creatingInspections: this.creatingInspections?.map((i) => i.id) ?? [],
            search: {
              page: 0,
              pageSize: 1000,
              filters: [
                { type: InspectionFilterType.personal, value: '100' },
                { type: InspectionFilterType.status, value: `-50` },
                {
                  type: InspectionFilterType.applicationSubType,
                  value: this.isIntallation ? `${ApplicationSubType.ParentInstallation}` : ''
                },
                ...this.additionalFilters
              ]
            }
          })
        )
      )
      .subscribe((inspections) => {
        inspections.filter((i) => i.status >= 0).forEach((i) => (i.isNew = true));
        this.creatingInspections = inspections;
        if (!this.creatingInspections?.filter((i) => i.status === -50).length) this.alive = false;
      });
  }

  sort(orderBy: InspectionOrderField) {
    this.resetPagination();
    this.filter.order = orderBy;
    this.filteredInspections = [];
    this.getInspections().subscribe(
      (inspections) => (this.filteredInspections = this.offlineInspection ? [this.offlineInspection, ...inspections] : inspections)
    );
  }

  applyFilters() {
    this.resetPagination();
    this.filteredInspections = [];
    this.getInspections().subscribe(
      (inspections) => (this.filteredInspections = this.offlineInspection ? [this.offlineInspection, ...inspections] : inspections)
    );
  }

  resetFilters() {
    this.showSearch = false;
    this.filter = {};
    this.applyFilters();
  }

  loadNextPage() {
    if (this.loading || this.isListComplete) return;

    this.page++;
    this.getInspections().subscribe((inspections) => {
      if (!inspections.length) {
        this.isListComplete = true;
      }
      this.filteredInspections.push(...inspections);
    });
  }

  public openMenu(): void {
    this.modal.modalFooter = false;
    this.modal.modalHeader = false;
    this.modal.modalMessage = false;
    this.modal.open(InspectionListMenuComponent);
  }

  openFilters(): void {
    this.filterModal.hasClassModal = true;
    this.filterModal.open(InspectionFilterModalComponent, {
      filter: this.filter,
      people: this.people,
      productLines: this.productLines,
      statusList: this.statusList,
      localEntities: this.localEntities,
      equipmentTypes: this.equipmentTypes
    });
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.gotoCreateEmptyInspectionPage();
        break;
      case 2:
        this.gotoCreateInspectionFromModelPage();
        break;
      default:
    }
  }

  public gotoCreateEmptyInspectionPage() {
    return this.router.navigate([this.inspectionsRoute, 'new', false]);
  }

  public gotoCreateInspectionFromModelPage() {
    //return this.router.navigate([this.inspectionsRoute,'new','fromModel']);
    return this.router.navigate([this.inspectionsRoute, 'selectMachine']);
  }

  viewReport(inspection: Inspection) {
    this.inspectionService.viewReport(inspection.reportItemId);
  }

  refreshPage() {
    this.refresh();
  }

  exportInspections() {
    this.inspectionService.exportInspections({ ...this.search, page: 0, pageSize: 1000000 });
  }

  goToBack(): void {
    this.machineSerial
      ? this.router.navigate([this.appRoute, 'machines', this.machineSerial])
      : this.clientNumber
      ? this.router.navigate([this.appRoute, 'customers', this.clientNumber])
      : this.router.navigate([this.appRoute]);
  }

  get inspections() {
    if (!this.creatingInspections && !this.filteredInspections) return null;
    return [...(this.creatingInspections ?? []), ...(this.filteredInspections ?? [])];
  }

  get inspectionsLabel() {
    return `home.inspection-${this.currentAppType}-list`;
  }

  get isIntallation() {
    return this.currentAppType === ApplicationType.Installation;
  }

  get filtersApplied() {
    return !!Object.keys(this.filter).length;
  }

  private initData() {
    this.peopleService.getPeople().subscribe((people) => (this.people = _.sortBy(people, ['name'])));
    this.machineFamillyService.getCurrentScopeProductLines().subscribe((productLines) => (this.productLines = productLines));
    this.localEntityService.getLocalEntities().subscribe((localEntities) => (this.localEntities = localEntities));
    this.machineFamillyService.getModels().subscribe((models) => (this.equipmentTypes = models));
    this.statusList = this.inspectionService.getStatusList();
  }

  private getInspections(): Observable<Inspection[]> {
    this.processFilters();
    this.loading = true;
    return this.inspectionService.getFilteredInspections(this.search).pipe(
      tap(() => {
        this.loading = false;
        this.inspectionService.filter = this.filter;
      })
    );
  }

  private processFilters() {
    const filters: SearchFilter[] = [];
    for (const key in this.filter) {
      const value = this.filter[key];
      if (value) {
        filters.push({ type: InspectionFilterType[key], value });
      }
    }
    this.search = {
      page: this.page,
      pageSize: this.pageSize,
      filters: [...this.defaultFilters, ...this.additionalFilters, ...filters],
      minimumEtag: null
    };
  }

  private resetPagination() {
    this.page = 0;
    this.isListComplete = false;
  }

  ngOnDestroy() {
    this.alive = false;
  }
}
